<script setup lang="ts">
import { WidgetBreadcrumbs } from '~/components/widget/breadcrumbs'
import { WidgetSeoContent } from '~/components/widget/seo'
import { WidgetBankPageRateChartNbkr } from '~/components/widget/bank-page'
import type { TExchangeRatesResponse } from '~/components/widget/sidebar-rates'
import { WidgetFaqList } from '~/components/widget/faq-list'

const { t } = useI18n()
const route = useRoute()
const { pathWithoutLocale } = useLocalizedPath()

const currency = computed(() =>
  Array.isArray(route.params.currency) ? route.params.currency[0] : route.params.currency
)

const currencyStore = useCurrencyStore()
const { list } = storeToRefs(currencyStore)

const selectedCurrency = computed(() =>
  list.value.find((i) => i.code.toLowerCase() === currency.value)
)

watchEffect(() => {
  if (list.value.length > 0 && !selectedCurrency.value) {
    showError({
      statusCode: 404
    })
  }
})

const urlBestRates = '/rates/widgets'
const { data } = useApi<TExchangeRatesResponse>(() => urlBestRates, {
  key: `rates-best-${currency.value}-rates-nbkr`
})

const centralBankRates = computed(() => data.value?.centralBankRates?.[currency.value] ?? null)

const breadcrumbs = computed(() => [
  { title: t('common.exchange_rates'), url: '/rates' },
  { title: t('page.rates.nbkr.breadcrumbs_title'), url: '/rates/nbkr' },
  { title: selectedCurrency.value?.title || t('common.currency'), url: route.path }
])

const rateText = computed(() =>
  t('page.rates.nbkr.currency.rate_today', {
    currency: selectedCurrency.value?.title || '',
    rate: centralBankRates.value || ''
  })
)
</script>

<template>
  <Main>
    <Container>
      <WidgetBreadcrumbs :links="breadcrumbs" />
      <div class="grid grid-cols-1 gap-5 xl:grid-cols-4">
        <div class="space-y-5 xl:col-span-4">
          <WidgetSeoContent position="upper" />

          <ContentBlock v-if="selectedCurrency && centralBankRates" variant="white" class="text-lg">
            {{ rateText }}
          </ContentBlock>

          <ContentBlock variant="white">
            <WidgetBankPageRateChartNbkr :currency="currency" />
          </ContentBlock>

          <WidgetSeoContent position="bottom" />

          <WidgetFaqList
            :query-params="{
              'filter[faqable_url]': pathWithoutLocale
            }"
          />
        </div>
      </div>
    </Container>
  </Main>
</template>
